/**
 * Utilities for parsing and rendering mentions in text
 */

import type { ReactNode } from 'react';

import { normalizeEntityType } from './normalizeEntityType';
import type { EntityType, ParsedMention } from './types';

/** Matches a PAIRED or SELF-CLOSING entity tag in one atomic, ReDoS-safe pass.
 *  Group 1 = tag name (capturing, so the \1 backreference matches the closing
 *  tag; the `i` flag makes it case-insensitively match the lowercase `entity`
 *  the backend emits). Group 2 = attribute substring, bounded by [^>]*? (lazy,
 *  cannot cross `>`) — the same shape used by entitySanitize.ts:170; NO `.*` and
 *  NO (attr)* loop. Tail alternation matches self-closing `/>` (group 3 undefined)
 *  OR paired `>inner</tag>` where inner is [^<]* (group 3 = display name). Keeping
 *  inner as [^<]* is load-bearing: it rejects nested/unclosed tags exactly as before. */
const ENTITY_TAG_REGEX =
  /<(mention|NamedEntity|Entity)\b([^>]*?)(?:\/>|>([^<]*)<\/\1>)/gi;
/** Extract id from the attribute substring; anchored on start-or-whitespace so
 *  `data-id`/similar never misfire and `entity-id` is preferred over `id`. */
const ID_ATTR_RE = /(?:^|\s)(?:entity-id|id)="([^"]+)"/i;
/** Extract type; anchored so `data-type="x"` never misfires as `type`. */
const TYPE_ATTR_RE = /(?:^|\s)type="([^"]+)"/i;

/** UUID pattern for validation */
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Numeric ID pattern for validation */
const NUMERIC_ID_REGEX = /^\d+$/;

/**
 * Validates if a string is a valid ID (UUID or numeric).
 */
function isValidId(id: string): boolean {
  return UUID_REGEX.test(id) || NUMERIC_ID_REGEX.test(id);
}

/**
 * Parse HTML-style mentions from text. Supports paired tags
 * (`<mention type="X" entity-id="Y">Name</mention>`), self-closing tags
 * (`<entity id="Y" type="X"/>`), the rare no-type self-closing form
 * (`<entity id="Y"/>`), and attribute order is not significant (`id` may
 * come before or after `type`).
 *
 * @param text - Text containing mention tags
 * @returns Array of parsed mentions with their positions
 *
 * @example
 * ```ts
 * parseMentions('<mention type="teammate" entity-id="550e8400-e29b-41d4-a716-446655440000">John</mention>')
 * // => [{ type: 'ahoy_user', entity_id: '550e8400-e29b-41d4-a716-446655440000', name: 'John', startIndex: 0, endIndex: 89 }]
 * ```
 */
export function parseMentions(text: string): ParsedMention[] {
  const mentions: ParsedMention[] = [];
  let match: RegExpExecArray | null;

  // Reset regex state
  ENTITY_TAG_REGEX.lastIndex = 0;

  while ((match = ENTITY_TAG_REGEX.exec(text)) !== null) {
    const attrs = match[2];
    const name = match[3];
    const isPaired = name !== undefined;

    // Skip mentions with invalid IDs (entity_id should be UUID or numeric)
    const idMatch = ID_ATTR_RE.exec(attrs);
    const entityId = idMatch?.[1];
    if (!entityId || !isValidId(entityId)) {
      continue;
    }

    const typeMatch = TYPE_ATTR_RE.exec(attrs);
    const rawType = typeMatch?.[1];

    // A paired tag with no type attribute stays ignored, exactly as before.
    if (isPaired && rawType === undefined) {
      continue;
    }

    // Normalize legacy types to ahoy_-prefixed types
    // Note: normalizeEntityType handles unknown types by returning the original value
    // for backward compatibility, so we trust its output without additional validation
    const resolvedType: EntityType =
      rawType !== undefined
        ? normalizeEntityType(rawType)
        : // self-closing tags carry no type attribute; the store resolves the entity via orgId. Skip normalizeEntityType to avoid a console.warn on ''.
          ('' as EntityType);

    mentions.push({
      type: resolvedType,
      entity_id: entityId,
      name: name ?? '',
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    });
  }

  return mentions;
}

/**
 * Render text with mentions by splitting into text and mention segments.
 * Text segments are rendered as React text nodes to prevent XSS.
 *
 * @param text - Text containing mention tags
 * @param renderMention - Callback to render each mention
 * @returns Array of React nodes (text strings and rendered mentions)
 *
 * @example
 * ```tsx
 * renderTextWithMentions(
 *   'Hello <mention type="user" entity-id="550e8400-e29b-41d4-a716-446655440000">John</mention>!',
 *   (mention) => <EntityBadgeById key={mention.startIndex} entityId={mention.entity_id} orgId={orgId} variant="inline" />
 * )
 * ```
 */
export function renderTextWithMentions(
  text: string,
  renderMention: (mention: ParsedMention) => ReactNode
): ReactNode[] {
  const mentions = parseMentions(text);

  if (mentions.length === 0) {
    // No mentions, return text as-is (safe - will be escaped by React)
    return [text];
  }

  const result: ReactNode[] = [];
  let lastIndex = 0;

  for (const mention of mentions) {
    // Add text before this mention (React will escape it automatically)
    if (mention.startIndex > lastIndex) {
      result.push(text.slice(lastIndex, mention.startIndex));
    }

    // Add the rendered mention
    result.push(renderMention(mention));

    lastIndex = mention.endIndex;
  }

  // Add remaining text after last mention
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result;
}
